"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { PageLoadingStateV2 } from "./maintainer/page-loading-state-v2";
import { Z_INDEX } from "./z-index";

export interface ScreenCenterLoaderV2Props {
  open: boolean;
  onClose?: () => void;
  keyboard?: boolean;
  className?: string;
  loaderSize?: "sm" | "md" | "lg";
  backdrop?: boolean;
}

export function ScreenCenterLoaderV2({
  open,
  onClose,
  keyboard = true,
  className,
  loaderSize = "lg",
  backdrop = true,
}: ScreenCenterLoaderV2Props) {
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  React.useEffect(() => {
    if (!open || !keyboard || !onClose) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [keyboard, onClose, open]);

  React.useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!mounted || !open) {
    return null;
  }

  return createPortal(
    <div
      className={cn("fixed inset-0 flex items-center justify-center", className)}
      style={{ zIndex: Z_INDEX.MODAL }}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      {backdrop ? (
        <button
          type="button"
          aria-label="Cancelar carga"
          className="absolute inset-0 bg-black/55 backdrop-blur-[6px] dark:bg-black/65"
          style={{ zIndex: Z_INDEX.MODAL_BACKDROP }}
          onClick={onClose}
        />
      ) : null}
      <div className="relative" style={{ zIndex: Z_INDEX.MODAL }}>
        <PageLoadingStateV2 visualOnly loaderSize={loaderSize} />
      </div>
    </div>,
    document.body
  );
}
